import { action, autorun, computed, makeObservable, observable } from 'mobx'

import type { FileMeta } from '@jotta/utils/files'

import { FileConflictQueue } from '../FileConflictDialog/FileConflictStore'

import { BaseActionManager } from './BaseActionManager'
import { FileUploadAction } from './FileUploadAction'

export class FileUploadManager extends BaseActionManager<FileUploadAction> {
  private _conflicts = new FileConflictQueue()

  public get conflicts() {
    return this._conflicts
  }

  private _open = false

  public get open() {
    return this._open
  }

  public set open(open: boolean) {
    this._open = open
    if (!open) {
      this.detailsViewOpen = false
    }
  }

  private _detailsViewOpen = false

  public get detailsViewOpen() {
    return this._detailsViewOpen
  }

  public set detailsViewOpen(open: boolean) {
    this._detailsViewOpen = open
  }

  public createUploadAction(file: FileMeta, path: string) {
    this.open = true
    const upload = new FileUploadAction(file, path, this.conflicts)
    this.addAction(upload)
    return upload
  }

  public upload(file: FileMeta, path: string) {
    return this.createUploadAction(file, path).asPromise()
  }

  public cancel() {
    this.clear()
    this.open = false
  }

  public clear() {
    super.clear()
    this._conflicts.clear()
  }

  public get hasPendingUploads() {
    return this.actions.some(upload => upload.isUploading)
  }

  public get hasPendingPreparations() {
    return this.actions.some(upload => upload.isPending)
  }

  constructor() {
    super()
    makeObservable<FileUploadManager, '_open' | '_detailsViewOpen'>(this, {
      _open: observable,
      _detailsViewOpen: observable,
      open: computed,
      detailsViewOpen: computed,
      createUploadAction: action.bound,
      upload: action.bound,
      cancel: action.bound,
      hasPendingUploads: computed,
      hasPendingPreparations: computed,
    })

    autorun(() => {
      if (this._conflicts.isCancelled) {
        this.cancel()
      }
    })

    autorun(() => {
      if (this.hasCompleted) {
        this.conflicts.clear()
      }
    })

    // single retry of any upload conflicts when no pending uploads
    autorun(() => {
      if (!this.hasPendingUploads) {
        this.actions
          .filter(value => value.isUploadConflict && value.startCount < 2)
          .forEach(value => value.retry())
      }
    })

    autorun(() => {
      if (this.total > 1) {
        this.conflicts.hasMultiple = true
      }
    })
  }
}

export const fileUploadManager = new FileUploadManager()
